import { stringify } from "qs";
import { emptyApi } from "./emptyApi";

export type DiscountType = "PERCENT" | "ABSOLUTE";
export type DurationType = "FOREVER" | "MONTHLY";

export type AdminCoupon = {
  uuid: string;
  coupon_code: string;
  description: string | null;
  valid_from: string;
  valid_till: string;
  max_discount: number;
  is_active: boolean;
  is_valid: boolean;
  is_free_task_coupon: boolean;
  created_at: string;
};

export type AdminCouponListItem = AdminCoupon & {
  task_category_mapping_count: number;
  stripe_coupon_count: number;
};

export type TaskTemplateCategory = {
  uuid: string;
  title: string;
};

export type SubscriptionPlan = {
  uuid: string;
  name: string;
  amount: string | null;
  stripe_price_id: string;
};

export type CouponTaskCategoryMapping = {
  uuid: string;
  task_template_category: TaskTemplateCategory;
  discount_type: DiscountType;
  discount_rate: number | null;
  allowed_redemption: number | null;
  expiry: string | null;
  is_free_task_coupon: boolean;
};

export type CouponStripeCoupon = {
  uuid: string;
  stripe_coupon_id: string;
  subscription_plan: SubscriptionPlan;
  discount_type: DiscountType;
  discount_rate: number;
  duration_type: DurationType;
  duration_period: number;
};

export type AdminCouponDetails = AdminCoupon & {
  task_category_mappings: CouponTaskCategoryMapping[];
  stripe_coupons: CouponStripeCoupon[];
};

type AdminCouponListResponse = {
  coupons: AdminCouponListItem[];
  total_pages: number;
  current_page: number;
  total_count: number;
  per_page: number;
};

type CouponOptions = {
  task_template_categories: TaskTemplateCategory[];
  subscription_plans: SubscriptionPlan[];
};

export type CouponPayload = {
  coupon_code?: string;
  description?: string;
  valid_from?: string;
  valid_till?: string;
  max_discount?: number;
  is_active?: boolean;
  is_free_task_coupon?: boolean;
};

export type TaskCategoryMappingPayload = {
  task_template_category_id?: string;
  discount_type?: DiscountType;
  discount_rate?: number;
  expiry?: string;
  allowed_redemption?: number | null;
  is_free_task_coupon?: boolean;
};

export type StripeCouponPayload = {
  stripe_coupon_id?: string;
  stripe_subscription_data_id?: string;
  discount_type?: DiscountType;
  discount_rate?: number;
  duration_type?: DurationType;
  duration_period?: number;
};

const COUPONS_URL = "/api/inkle/users/coupons";

export const couponAdminApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAdminCoupons: build.query<
      AdminCouponListResponse,
      { search_term?: string | null; page_num?: number }
    >({
      query: (params) => {
        const queryUrl = stringify(params, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return { url: `${COUPONS_URL}/${queryUrl}` };
      },
      providesTags: ["ADMIN_COUPONS"],
    }),

    getAdminCouponOptions: build.query<CouponOptions, void>({
      query: () => ({ url: `${COUPONS_URL}/options/` }),
    }),

    getAdminCoupon: build.query<AdminCouponDetails, { couponId: string }>({
      query: ({ couponId }) => ({ url: `${COUPONS_URL}/${couponId}/` }),
      providesTags: ["ADMIN_COUPONS"],
    }),

    createAdminCoupon: build.mutation<AdminCouponDetails, CouponPayload>({
      query: (body) => ({ url: `${COUPONS_URL}/`, method: "POST", body }),
      invalidatesTags: ["ADMIN_COUPONS"],
    }),

    updateAdminCoupon: build.mutation<
      AdminCouponDetails,
      { couponId: string; payload: CouponPayload }
    >({
      query: ({ couponId, payload }) => ({
        url: `${COUPONS_URL}/${couponId}/`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["ADMIN_COUPONS"],
    }),

    createCouponTaskCategoryMapping: build.mutation<
      CouponTaskCategoryMapping,
      { couponId: string; payload: TaskCategoryMappingPayload }
    >({
      query: ({ couponId, payload }) => ({
        url: `${COUPONS_URL}/${couponId}/task-category-mappings/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["ADMIN_COUPONS"],
    }),

    updateCouponTaskCategoryMapping: build.mutation<
      CouponTaskCategoryMapping,
      {
        couponId: string;
        mappingId: string;
        payload: TaskCategoryMappingPayload;
      }
    >({
      query: ({ couponId, mappingId, payload }) => ({
        url: `${COUPONS_URL}/${couponId}/task-category-mappings/${mappingId}/`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["ADMIN_COUPONS"],
    }),

    deleteCouponTaskCategoryMapping: build.mutation<
      void,
      { couponId: string; mappingId: string }
    >({
      query: ({ couponId, mappingId }) => ({
        url: `${COUPONS_URL}/${couponId}/task-category-mappings/${mappingId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["ADMIN_COUPONS"],
    }),

    createCouponStripeCoupon: build.mutation<
      CouponStripeCoupon,
      { couponId: string; payload: StripeCouponPayload }
    >({
      query: ({ couponId, payload }) => ({
        url: `${COUPONS_URL}/${couponId}/stripe-coupons/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["ADMIN_COUPONS"],
    }),

    updateCouponStripeCoupon: build.mutation<
      CouponStripeCoupon,
      { couponId: string; mappingId: string; payload: StripeCouponPayload }
    >({
      query: ({ couponId, mappingId, payload }) => ({
        url: `${COUPONS_URL}/${couponId}/stripe-coupons/${mappingId}/`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["ADMIN_COUPONS"],
    }),

    deleteCouponStripeCoupon: build.mutation<
      void,
      { couponId: string; mappingId: string }
    >({
      query: ({ couponId, mappingId }) => ({
        url: `${COUPONS_URL}/${couponId}/stripe-coupons/${mappingId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["ADMIN_COUPONS"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAdminCouponsQuery,
  useGetAdminCouponOptionsQuery,
  useGetAdminCouponQuery,
  useCreateAdminCouponMutation,
  useUpdateAdminCouponMutation,
  useCreateCouponTaskCategoryMappingMutation,
  useUpdateCouponTaskCategoryMappingMutation,
  useDeleteCouponTaskCategoryMappingMutation,
  useCreateCouponStripeCouponMutation,
  useUpdateCouponStripeCouponMutation,
  useDeleteCouponStripeCouponMutation,
} = couponAdminApis;
